// One support conversation, shown inline beside the list: the whole thread as
// chat bubbles, claim / resolve / reopen up in the header, and a composer at
// the bottom (0141 claim/reply/resolve; 0315 urgency; 0335 reopen).
//
// ⚠ DATA WALL. The client appears by first name only, exactly as
// `v_support_thread_staff` gives it. Messages are the client's own words —
// nothing here looks up the client's phone, email or address.

import { Avatar, AvatarFallback } from "@bednbody/ui/components/avatar";
import { Badge } from "@bednbody/ui/components/badge";
import { Button } from "@bednbody/ui/components/button";
import { Textarea } from "@bednbody/ui/components/textarea";
import { formatTimeAgo } from "@bednbody/ui/lib/format";
import { cn } from "@bednbody/ui/lib/utils";
import { ArrowLeftIcon, PaperPlaneRightIcon, SparkleIcon } from "@phosphor-icons/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { type SupportMessage, useSupportActions, useSupportMessages } from "./support.hooks";
import {
  CATEGORY_LABEL,
  type SupportStatus,
  type SupportTicketRow,
  ticketStatusLabel,
  urgentReasonLabel,
} from "./support.model";

const REPLY_MAX = 2000;

const SENDER_LABEL = { client: "Client", ai: "Assistant", staff: "Team" } as const;

/** The status chip tones, shared with the list so a ticket reads the same in both. */
export const STATUS_TONE: Record<
  SupportStatus,
  "muted-soft" | "warning-soft" | "info-soft" | "success-soft"
> = {
  ai: "muted-soft",
  waiting: "warning-soft",
  open: "info-soft",
  resolved: "success-soft",
};

/**
 * A tinted avatar says what the conversation is about before it is read: red
 * for urgent, amber for payment, teal for booking, blue for account. Soft fills
 * with dark text — never white on a light fill, which fails contrast.
 */
export function avatarTone(category: string, urgent: boolean): string {
  if (urgent || category === "sos") return "bg-destructive/15 text-destructive";
  if (category === "payment") return "bg-gold/25 text-gold-foreground";
  if (category === "booking") return "bg-primary/15 text-primary-dark";
  if (category === "account") return "bg-info/15 text-info";
  return "bg-muted text-foreground";
}

/**
 * On a staff-authored thread (0320) the author also posts as 'staff' — the
 * reader tells "the person asking" from "the team answering" by sender_id
 * against the thread's staff_id, never by sender kind.
 */
function isAuthor(message: { sender_id: string | null }, ticket: SupportTicketRow): boolean {
  return ticket.author === "staff" && message.sender_id === ticket.staff_id;
}

function senderName(message: SupportMessage, ticket: SupportTicketRow): string {
  if (isAuthor(message, ticket)) {
    return `${ticket.staff_first_name?.trim() || "Staff"} (${ticket.staff_role ?? "staff"})`;
  }
  return SENDER_LABEL[message.sender] ?? message.sender;
}

const dayLabel = (iso: string) => {
  const date = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today.getTime() - 86_400_000);
  if (date.toDateString() === today.toDateString()) return "Today";
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return date.toLocaleDateString("en-PH", { weekday: "short", month: "short", day: "numeric" });
};

const clock = (iso: string) =>
  new Date(iso).toLocaleTimeString("en-PH", { hour: "numeric", minute: "2-digit" });

export function TicketThread({
  ticket,
  bookingRef,
  assignedName,
  canSetUrgency,
  hubName,
  isMine,
  onBack,
}: {
  ticket: SupportTicketRow;
  bookingRef: string | null;
  assignedName: string | null;
  /** Admin and superadmin — the SQL function refuses everyone else anyway. */
  canSetUrgency: boolean;
  /** The dispatch area that owns the inbox — null for an unhubbed enquiry. */
  hubName: string | null;
  /** The signed-in person holds this ticket. */
  isMine: boolean;
  /** Narrow screens show one pane at a time; this returns to the list. */
  onBack: () => void;
}) {
  const messages = useSupportMessages(ticket.id);
  const actions = useSupportActions(ticket.id);
  const [reply, setReply] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);
  const trimmed = reply.trim();
  const resolved = ticket.status === "resolved";
  // The client closed it (0173). Not the team's to reopen or answer — the client
  // starts a new conversation if they need one — so it is a read-only record.
  const closedByClient = ticket.withdrawn_at !== null && !resolved;
  const client =
    ticket.author === "staff"
      ? `${ticket.staff_first_name?.trim() || "a staff member"} (${ticket.staff_role ?? "staff"})`
      : ticket.client_first_name?.trim() || "the client";
  const initial = client.replace(/^a /, "").charAt(0).toUpperCase();
  const count = messages.data?.length ?? 0;

  // A draft belongs to the conversation it was typed in.
  useEffect(() => setReply(""), [ticket.id]);
  // Land on the newest message, and follow new ones as they arrive.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [ticket.id, count]);

  // Bubbles grouped by day, each knowing whether it starts a run from one sender.
  const days = useMemo(() => {
    const out: { label: string; items: { message: SupportMessage; first: boolean }[] }[] = [];
    let previous: SupportMessage | null = null;
    for (const message of messages.data ?? []) {
      const label = dayLabel(message.created_at);
      let day = out.at(-1);
      if (!day || day.label !== label) {
        day = { label, items: [] };
        out.push(day);
        previous = null;
      }
      day.items.push({
        message,
        first:
          !previous ||
          previous.sender !== message.sender ||
          previous.sender_id !== message.sender_id,
      });
      previous = message;
    }
    return out;
  }, [messages.data]);

  const notify = (done: string) => ({
    onSuccess: () => toast.success(done),
    onError: (error: Error) => toast.error(error.message),
  });

  function send() {
    if (!trimmed) return;
    actions.reply.mutate(trimmed, {
      onSuccess: () => setReply(""),
      onError: (error) => toast.error("Couldn't send the reply", { description: error.message }),
    });
  }

  return (
    <section className="flex h-full min-h-0 flex-col" aria-label={`Conversation ${ticket.ref}`}>
      <header className="space-y-3 border-b border-border bg-linear-to-r from-primary/10 via-primary/5 to-transparent p-4">
        <div className="flex items-center gap-3">
          <Button
            aria-label="Back to conversations"
            className="lg:hidden"
            onClick={onBack}
            size="icon"
            variant="ghost"
          >
            <ArrowLeftIcon />
          </Button>
          <Avatar size="lg">
            <AvatarFallback className={cn("font-bold", avatarTone(ticket.category, ticket.urgent))}>
              {initial}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-xl font-bold text-foreground">{client}</h2>
            <p className="truncate text-sm font-medium text-muted-foreground">
              <span className="font-mono">{ticket.ref}</span> · {CATEGORY_LABEL[ticket.category]}
              {bookingRef ? ` · ${bookingRef}` : ""}
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap justify-end gap-2">
            {closedByClient ? null : resolved ? (
              <Button
                loading={actions.reopen.isPending}
                onClick={() => actions.reopen.mutate(undefined, notify("Ticket reopened."))}
                variant="outline"
              >
                Reopen
              </Button>
            ) : (
              <>
                {isMine ? null : (
                  <Button
                    loading={actions.claim.isPending}
                    onClick={() => actions.claim.mutate(undefined, notify("You have this ticket."))}
                  >
                    {ticket.assigned_staff_id ? "Take over" : "Claim"}
                  </Button>
                )}
                <Button
                  loading={actions.resolve.isPending}
                  onClick={() => actions.resolve.mutate(undefined, notify("Ticket resolved."))}
                  variant="outline"
                >
                  Resolve
                </Button>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge
            className="h-6 text-sm"
            shape="soft"
            variant={closedByClient ? "muted-soft" : STATUS_TONE[ticket.status]}
          >
            {ticketStatusLabel(ticket)}
          </Badge>
          {ticket.urgent && !closedByClient ? (
            <Badge className="h-6 text-sm" shape="soft" variant="destructive-soft">
              {urgentReasonLabel(ticket.urgent_reason) ?? "Urgent"}
            </Badge>
          ) : null}
          {isMine ? (
            <Badge className="h-6 text-sm" shape="soft" variant="success-soft">
              You have this
            </Badge>
          ) : assignedName ? (
            <Badge className="h-6 text-sm" shape="soft" variant="info-soft">
              {assignedName}
            </Badge>
          ) : (
            <Badge className="h-6 text-sm" shape="soft" variant="warning-soft">
              Unassigned
            </Badge>
          )}
          <span className="text-sm text-muted-foreground">
            Opened {formatTimeAgo(ticket.created_at)}
            {hubName ? ` · ${hubName}` : ""}
          </span>
        </div>
      </header>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto bg-linear-to-b from-muted/40 to-primary/5 p-4">
        {messages.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading the conversation…</p>
        ) : messages.isError ? (
          <p className="text-sm text-muted-foreground">The conversation could not be loaded.</p>
        ) : count === 0 ? (
          <p className="text-sm text-muted-foreground">No messages yet.</p>
        ) : (
          days.map((day) => (
            <div key={day.label} className="space-y-1.5">
              <p className="py-1 text-center text-sm font-semibold text-muted-foreground">
                {day.label}
              </p>
              {day.items.map(({ message, first }) => {
                const team = message.sender === "staff" && !isAuthor(message, ticket);
                const assistant = message.sender === "ai";
                return (
                  <div
                    key={message.id}
                    className={cn(
                      "flex flex-col",
                      team ? "items-end" : "items-start",
                      first ? "pt-2" : null,
                    )}
                  >
                    {first ? (
                      <p className="mb-1 flex items-center gap-2 px-1 text-sm font-medium text-muted-foreground">
                        {team ? null : (
                          <Avatar size="sm">
                            <AvatarFallback
                              className={cn(
                                "text-xs font-bold",
                                assistant
                                  ? "bg-info/15 text-info"
                                  : avatarTone(ticket.category, ticket.urgent),
                              )}
                            >
                              {assistant ? (
                                <SparkleIcon className="size-3.5" weight="fill" />
                              ) : (
                                initial
                              )}
                            </AvatarFallback>
                          </Avatar>
                        )}
                        {senderName(message, ticket)}
                      </p>
                    ) : null}
                    <div
                      className={cn(
                        "max-w-[80%] rounded-2xl px-4 py-2.5 text-base leading-relaxed",
                        team
                          ? "rounded-br-md bg-primary text-primary-foreground"
                          : assistant
                            ? "rounded-bl-md border border-info/25 bg-info/10 text-foreground"
                            : "rounded-bl-md border border-border bg-card text-foreground shadow-xs",
                      )}
                    >
                      <p className="whitespace-pre-wrap">{message.body}</p>
                    </div>
                    <p className="mt-1 px-1 text-sm text-muted-foreground">
                      {clock(message.created_at)}
                    </p>
                  </div>
                );
              })}
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      <footer className="space-y-3 border-t border-border bg-card p-4">
        {closedByClient ? (
          <p className="text-sm text-muted-foreground">
            The client closed this conversation
            {ticket.withdrawn_at ? ` ${formatTimeAgo(ticket.withdrawn_at)}` : ""}. It stays here for
            the record; they can start a new one from the app.
          </p>
        ) : resolved ? (
          <p className="text-sm text-muted-foreground">
            Resolved{ticket.resolved_at ? ` ${formatTimeAgo(ticket.resolved_at)}` : ""}. Reopen it
            to reply.
          </p>
        ) : (
          <div className="space-y-1.5">
            <div className="flex items-end gap-2 rounded-card border border-border bg-background p-2 focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/30">
              <Textarea
                aria-label={`Reply to ${client}`}
                className="max-h-40 min-h-10 resize-none border-0 bg-transparent text-base shadow-none focus-visible:ring-0 md:text-base"
                maxLength={REPLY_MAX}
                placeholder={`Reply to ${client}…`}
                rows={2}
                value={reply}
                onChange={(event) => setReply(event.target.value)}
                onKeyDown={(event) => {
                  // Enter sends, Shift+Enter is a new line — the Messenger habit.
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    send();
                  }
                }}
              />
              <Button
                aria-label="Send reply"
                disabled={!trimmed}
                loading={actions.reply.isPending}
                onClick={send}
                size="icon-lg"
              >
                <PaperPlaneRightIcon weight="fill" />
              </Button>
            </div>
            <p className="px-1 text-sm text-muted-foreground">
              Enter to send · Shift+Enter for a new line
            </p>
          </div>
        )}

        {canSetUrgency && !resolved && !closedByClient ? (
          <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
            <span className="text-sm text-muted-foreground">
              {ticket.urgent_override === null
                ? "Urgency follows the rules."
                : ticket.urgent_override
                  ? "Flagged urgent by hand."
                  : "Unflagged by hand."}
            </span>
            {ticket.urgent_override !== true ? (
              <Button
                size="sm"
                variant="outline"
                loading={actions.setUrgency.isPending}
                onClick={() => actions.setUrgency.mutate(true, notify("Flagged urgent."))}
              >
                Flag urgent
              </Button>
            ) : null}
            {ticket.urgent_override !== false ? (
              <Button
                size="sm"
                variant="outline"
                loading={actions.setUrgency.isPending}
                onClick={() => actions.setUrgency.mutate(false, notify("Marked not urgent."))}
              >
                Not urgent
              </Button>
            ) : null}
            {ticket.urgent_override !== null ? (
              <Button
                size="sm"
                variant="ghost"
                loading={actions.setUrgency.isPending}
                onClick={() =>
                  actions.setUrgency.mutate(null, notify("Urgency follows the rules again."))
                }
              >
                Back to automatic
              </Button>
            ) : null}
          </div>
        ) : null}
      </footer>
    </section>
  );
}
